import type { AuthUser } from '@/types/auth';

const TOKEN_KEY = 'aryan-token';
const REFRESH_KEY = 'aryan-refresh';
const USER_KEY = 'aryan-user';
const PERMISSIONS_KEY = 'aryan-permissions';

function safeGet(key: string): string | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeSet(key: string, value: string): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // Silent fail.
  }
}

function safeRemove(key: string): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(key);
  } catch {
    // Silent fail.
  }
}

export const tokenStorage = {
  read: (): string | null => safeGet(TOKEN_KEY),
  write: (value: string): void => {
    safeSet(TOKEN_KEY, value);
  },
  clear: (): void => {
    safeRemove(TOKEN_KEY);
  },
};

export const refreshStorage = {
  read: (): string | null => safeGet(REFRESH_KEY),
  write: (value: string): void => {
    safeSet(REFRESH_KEY, value);
  },
  clear: (): void => {
    safeRemove(REFRESH_KEY);
  },
};

export const userStorage = {
  read: (): AuthUser | null => {
    const raw = safeGet(USER_KEY);
    if (raw === null) return null;
    try {
      const parsed: unknown = JSON.parse(raw);
      if (
        typeof parsed === 'object' &&
        parsed !== null &&
        'id' in parsed &&
        'email' in parsed &&
        'fullName' in parsed &&
        'roles' in parsed
      ) {
        // Backfill Phase-6 fields when an older Phase-3 payload is rehydrated, so
        // the rest of the app can rely on AuthUser having the full Phase-6 shape.
        const candidate = parsed as Record<string, unknown>;
        const employeeId = typeof candidate.employeeId === 'string' ? candidate.employeeId : null;
        const passwordChangeRequired = candidate.passwordChangeRequired === true;
        return {
          ...(parsed as AuthUser),
          employeeId,
          passwordChangeRequired,
        };
      }
      return null;
    } catch {
      return null;
    }
  },
  write: (value: AuthUser): void => {
    safeSet(USER_KEY, JSON.stringify(value));
  },
  clear: (): void => {
    safeRemove(USER_KEY);
  },
};

export const permissionsStorage = {
  read: (): readonly string[] => {
    const raw = safeGet(PERMISSIONS_KEY);
    if (raw === null) return [];
    try {
      const parsed: unknown = JSON.parse(raw);
      if (Array.isArray(parsed) && parsed.every((p) => typeof p === 'string')) {
        return parsed;
      }
      return [];
    } catch {
      return [];
    }
  },
  write: (value: readonly string[]): void => {
    safeSet(PERMISSIONS_KEY, JSON.stringify(value));
  },
  clear: (): void => {
    safeRemove(PERMISSIONS_KEY);
  },
};

/** Clear all auth-related localStorage keys. Used by the 401 interceptor and logout(). */
export function clearAllAuthStorage(): void {
  tokenStorage.clear();
  refreshStorage.clear();
  userStorage.clear();
  permissionsStorage.clear();
}
